import type { ReactNode } from 'react'
import { AlertTriangle, IdCard, MapPin, User as UserIcon } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Skeleton } from '@/components/ui/skeleton'
import type { User } from '@/api/users'

interface UserDetailDrawerProps {
	user: User | undefined
	open: boolean
	isLoading: boolean
	onOpenChange: (open: boolean) => void
}

const statusConfig: Record<number, { label: string; variant: 'default' | 'destructive' | 'secondary' }> = {
	1: { label: '正常', variant: 'default' },
	0: { label: '禁用', variant: 'destructive' },
}

const genderConfig: Record<number, string> = {
	0: '未知',
	1: '男',
	2: '女',
}

function Field({ label, value }: { label: string; value: ReactNode }) {
	return (
		<div className="space-y-1">
			<div className="text-xs text-muted-foreground">{label}</div>
			<div className="break-words text-sm">{value || '-'}</div>
		</div>
	)
}

function Section({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
	return (
		<section className="space-y-3">
			<div className="flex items-center gap-2 text-sm font-semibold">
				{icon}
				<span>{title}</span>
			</div>
			<div className="grid gap-4 sm:grid-cols-2">{children}</div>
		</section>
	)
}

function ImageField({ label, value }: { label: string; value?: string }) {
	if (!value) {
		return <Field label={label} value="-" />
	}

	return (
		<div className="space-y-2">
			<div className="text-xs text-muted-foreground">{label}</div>
			<a href={value} target="_blank" rel="noreferrer" className="block overflow-hidden rounded-md border bg-muted">
				<img src={value} alt={label} className="h-28 w-full object-cover transition-opacity hover:opacity-90" />
			</a>
		</div>
	)
}

export function UserDetailDrawer({ user, open, isLoading, onOpenChange }: UserDetailDrawerProps) {
	const statusInfo = user ? statusConfig[user.status] || { label: '未知', variant: 'secondary' as const } : null

	return (
		<Sheet open={open} onOpenChange={onOpenChange}>
			<SheetContent className="w-full overflow-y-auto sm:max-w-2xl">
				<SheetHeader>
					<SheetTitle>用户详情</SheetTitle>
				</SheetHeader>

				{isLoading ? (
					<div className="mt-6 space-y-4">
						{Array.from({ length: 8 }).map((_, index) => (
							<Skeleton key={index} className="h-6 w-full" />
						))}
					</div>
				) : user ? (
					<div className="mt-6 space-y-6">
						<div className="flex items-start gap-4">
							<div className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-md bg-muted">
								{user.avatar ? (
									<img src={user.avatar} alt={user.nickname || user.username} className="h-full w-full object-cover" />
								) : (
									<UserIcon className="h-6 w-6 text-muted-foreground" />
								)}
							</div>
							<div className="min-w-0 flex-1">
								<div className="flex flex-wrap items-center gap-2">
									<h2 className="truncate text-lg font-semibold">{user.nickname || user.username}</h2>
									{statusInfo && <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>}
									{user.is_super_admin && <Badge variant="outline">超级管理员</Badge>}
								</div>
								<p className="mt-1 text-sm text-muted-foreground">{user.username}</p>
							</div>
						</div>

						<Separator />

						<Section icon={<UserIcon className="h-4 w-4" />} title="基础信息">
							<Field label="用户ID" value={user.id} />
							<Field label="手机号" value={user.phone} />
							<Field label="邮箱" value={user.email} />
							<Field label="微信号" value={user.wechat} />
							<Field label="性别" value={genderConfig[user.gender] || '未知'} />
							<Field label="部门ID" value={user.department_id || '-'} />
							<Field label="CRMEB UID" value={user.crmeb_uid || '-'} />
							<Field label="保证金余额" value={`¥${Number(user.deposit_balance || 0).toFixed(2)}`} />
						</Section>

						<Separator />

						<Section icon={<IdCard className="h-4 w-4" />} title="身份资料">
							<Field label="身份证号" value={user.card_no} />
							<Field label="业务自律书" value={user.self_discipline ? <a className="text-primary hover:underline" href={user.self_discipline} target="_blank" rel="noreferrer">查看文件</a> : '-'} />
							<ImageField label="身份证正面照" value={user.card_front} />
							<ImageField label="身份证背面照" value={user.card_back} />
						</Section>

						<Separator />

						<Section icon={<AlertTriangle className="h-4 w-4" />} title="紧急联系人">
							<Field label="联系人" value={user.emergency_contact} />
							<Field label="联系电话" value={user.emergency_mobile} />
						</Section>

						<Separator />

						<Section icon={<MapPin className="h-4 w-4" />} title="其他资料">
							<Field label="地址" value={user.address} />
							<Field label="备注" value={user.remark} />
							<Field label="创建时间" value={user.created_at} />
							<Field label="更新时间" value={user.updated_at} />
						</Section>

					</div>
				) : (
					<div className="mt-6 text-sm text-muted-foreground">暂无用户详情</div>
				)}
			</SheetContent>
		</Sheet>
	)
}
